import { AssignmentScoreType } from '../rest-interfaces/assignment';
import { ValidationRulesEngine } from '../validation-engine/validation-engine';
import { RawGradeScaleItem, ValidationRules, ValidationResult, Validator } from '../interfaces';
import { NumberUtil } from './number-util';
import { GradeScaleRI, GradeScaleItemRI, SpecialGradeScaleRI, SpecialGradeScaleItemRI } from '../rest-interfaces';
import roundToPrecision = NumberUtil.roundToPrecision;
import { I18nUtil } from './i18n-util';

const PLUS_MINUS_REGEX = /[+\-]$/;
const PRECISION = 6;

export namespace GradeScaleUtil {
  export type CalcRelationship = 'GRADESCALE' | 'LEVELS';

  export function buildGradeScaleHierarchyRIs(rawItems: Array<RawGradeScaleItem>): Array<GradeScaleRI> {
    return _buildGradeScaleHierarchyRIs(rawItems, false);
  }

  export function buildSpecialGradeScaleHierarchyRIs(rawItems: Array<RawGradeScaleItem>): Array<SpecialGradeScaleRI> {
    return <Array<SpecialGradeScaleRI>>_buildGradeScaleHierarchyRIs(rawItems, true);
  }

  function _buildGradeScaleHierarchyRIs(rawItems: Array<RawGradeScaleItem>, isSpecialScale: boolean): Array<GradeScaleRI> {
    let scaleToScaleObjectMap: Map<string, GradeScaleRI | SpecialGradeScaleRI> = new Map();
    let scaleToItemObjectsMap: Map<string, Array<GradeScaleItemRI | SpecialGradeScaleItemRI>> = new Map();

    for (let rawItem of rawItems) {
      let scaleUidKey: string = rawItem.gradeScaleUid;

      // creating GradeScaleRI object
      if (!scaleToScaleObjectMap.has(scaleUidKey)) {
        let scale: GradeScaleRI = {
          uid: rawItem.gradeScaleUid,
          name: rawItem.gradeScaleName,
          description: rawItem.gradeScaleDescription,
          gradeScaleType: rawItem.gradeScaleType,
          colorLevels: rawItem.colorLevels,
          numericMax: rawItem.numericMax,
          numericMin: rawItem.numericMin,
          numericDecimals: rawItem.numericDecimals,
          numericExtras: rawItem.numericExtras,
          conversionToNumeric: rawItem.conversionToNumeric,
          specialGradeScaleUid: rawItem.specialGradeScaleUid,
          gradeScaleItems: []
        };
        scaleToScaleObjectMap.set(scaleUidKey, scale);
      }

      // creating GradeScaleItemRI objects
      if (!scaleToItemObjectsMap.has(scaleUidKey)) {
        scaleToItemObjectsMap.set(scaleUidKey, []);
      }
      let createdItem = isSpecialScale ? buildSpecialGradeScaleItemRI(rawItem) : buildGradeScaleItemRI(rawItem);
      if (createdItem != null) {
        scaleToItemObjectsMap.get(rawItem.gradeScaleUid)!.push(createdItem);
      }
    }

    let scales = Array.from(scaleToScaleObjectMap.values()).map((scale: GradeScaleRI) => {
      scale.gradeScaleItems = sortGradeScaleItems(scaleToItemObjectsMap.get(scale.uid) || []);
      return scale;
    });
    return sortGradeScales(scales);
  }

  export function buildGradeScaleItemRI(rawItem: RawGradeScaleItem): GradeScaleItemRI | undefined {
    if (rawItem.gradeScaleItemUid != null) {
      return {
        uid: rawItem.gradeScaleItemUid,
        name: rawItem.gradeScaleItemName!,
        description: rawItem.gradeScaleItemDescription,
        color: rawItem.gradeScaleItemColor,
        numericCutOff: rawItem.gradeScaleItemNumericCutOff!,
        cutOffPercentage: rawItem.gradeScaleItemCutOffPercentage!,
        numericValue: rawItem.gradeScaleItemNumericValue!,
        percentValue: rawItem.gradeScaleItemPercentValue!,
        isProficient: rawItem.gradeScaleItemIsProficient === 1,
        displayPosition: rawItem.gradeScaleItemDisplayPosition
      };
    }
    return undefined;
  }

  export function buildSpecialGradeScaleItemRI(rawItem: RawGradeScaleItem): SpecialGradeScaleItemRI | undefined {
    let item: GradeScaleItemRI | undefined = buildGradeScaleItemRI(rawItem);
    if (item != null && rawItem.gradeScaleItemUid != null) {
      let specialItem: SpecialGradeScaleItemRI = <SpecialGradeScaleItemRI>item;
      specialItem.isAbsent = rawItem.gradeScaleItemIsAbsent === 1;
      specialItem.isCollected = rawItem.gradeScaleItemIsCollected === 1;
      specialItem.isExempt = rawItem.gradeScaleItemIsExempt === 1;
      specialItem.isIncomplete = rawItem.gradeScaleItemIsIncomplete === 1;
      specialItem.isLate = rawItem.gradeScaleItemIsLate === 1;
      specialItem.isMissing = rawItem.gradeScaleItemIsMissing === 1;
      specialItem.isScoreCodeOnAssignments = rawItem.gradeScaleItemIsScoreCodeOnAssignments === 1;
      specialItem.numericType = (rawItem.specialNumericType === 'Max' || rawItem.specialNumericType === 'Min' || rawItem.specialNumericType === 'Custom') ? <'MAX' | 'MIN' | 'CUSTOM'>rawItem.specialNumericType.toUpperCase() : undefined!;
      specialItem.numericValue = rawItem.specialNumericValue!;
      specialItem.percentValue = rawItem.specialPercentValue!;
      specialItem.displayPosition = rawItem.gradeScaleItemDisplayPosition;
      return specialItem;
    }
    return undefined;
  }

  // sort GradeScaleItems by descending order by default
  export function sortGradeScaleItems<T extends GradeScaleItemRI>(scaleItems: Array<T>): Array<T> {
    scaleItems.sort((a: T, b: T) => {
      // The sorting below follows the sorting used in PowerSchool SIS' Edit Grade Scale page.

      // 1. Sort by cutoff percentage in descending order
      if (a.cutOffPercentage !== b.cutOffPercentage) {
        return b.cutOffPercentage - a.cutOffPercentage;
      }

      // 2. Sort by display position in descending order.  The one marked display position > 0 is the default zero cutoff.
      let aDisplayPos = a.displayPosition || 0;
      let bDisplayPos = b.displayPosition || 0;
      if (aDisplayPos - bDisplayPos) {
        return bDisplayPos - aDisplayPos;
      }

      // 3. Sort by grade scale item name in ascending order
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());

    });
    return scaleItems;
  }

  // sort GradeScales by alphabetic order by default
  export function sortGradeScales<T extends GradeScaleRI>(scales: Array<T>): Array<T> {
    scales.sort((a: T, b: T) => {
      return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
    });
    return scales;
  }

  export function getHighestGradeScaleScore(gradeScale: GradeScaleRI): GradeScaleItemRI | undefined {
    if (gradeScale == null || gradeScale.gradeScaleItems == null || gradeScale.gradeScaleItems.length === 0) {
      return undefined;
    }
    // Grade scale items are sorted by cutOffPercentage first in descending order.
    return gradeScale.gradeScaleItems[0];
  }

  /**
   * Main Function used by UI (hopefully serverside can use it too).
   * GradeScaleScore -> TypedScore
   */
  export function convertGradeScaleItemToScoreOfType(gradeScaleItemToConvert: string | undefined,
    gradeScale: GradeScaleRI,
    convertTo: AssignmentScoreType,
    roundNumbers: boolean,
    calcRelationship: CalcRelationship = 'GRADESCALE',
    pointsPossible?: number): string | undefined {

    if (gradeScaleItemToConvert == null) {
      return;
    }

    let value: string | number | undefined;

    if (convertTo === 'GRADESCALE') {
      //Gradescale to gradescale is EASY!  Just return the item!!!
      return gradeScaleItemToConvert;
    }

    if (gradeScale.gradeScaleType === 'NUMERIC') {
      switch (convertTo) {
        case 'POINTS':
          if (pointsPossible == null) {
            return;
          }
          value = convertNumericGradeScaleItemToPoints(gradeScaleItemToConvert, gradeScale, calcRelationship, pointsPossible, roundNumbers);
          break;
        case 'PERCENT':
          value = convertNumericGradeScaleItemToPercent(gradeScaleItemToConvert, gradeScale, roundNumbers);
          break;
        case 'COLLECTED_ONLY':
          break;
        default:
          break;
      }
    } else {
      //gradeScale.gradeScaleType === 'ALPHA'
      switch (convertTo) {
        case 'POINTS':
          if (pointsPossible == null) {
            return;
          }
          value = convertGradeScaleItemToPoints(gradeScaleItemToConvert, gradeScale, pointsPossible, roundNumbers);
          break;
        case 'PERCENT':
          value = convertGradeScaleItemToPercent(gradeScaleItemToConvert, gradeScale, roundNumbers);
          break;
        case 'COLLECTED_ONLY':
          break;
        default:
          break;
      }
    }

    if (value == null) {
      return;
    }

    return String(value);
  }

  /**
   * Main Function used by UI (hopefully serverside can use it too).
   * TypedScore -> GradeScaleScore
   */
  export function convertScoreOfTypeToGradeScaleItem(scoreValue: string | undefined,
    gradeScale: GradeScaleRI,
    convertFrom: AssignmentScoreType,
    roundNumbers: boolean,
    calcRelationship: CalcRelationship = 'GRADESCALE',
    pointsPossible?: number): string | undefined {

    if (scoreValue == null) {
      return;
    }

    //This ONLY returns a GSI if the current scoreValue cannot be used as is.
    let specialGradeScaleItem: SpecialGradeScaleItemRI | undefined = getSpecialGradeScaleItem(scoreValue, gradeScale, convertFrom);
    if (specialGradeScaleItem != null) {
      let specialValue: string | number | undefined = convertSpecialGradeScaleItemTo(specialGradeScaleItem,
        gradeScale,
        'GRADESCALE',
        true,
        calcRelationship,
        pointsPossible);
      if (specialValue == null) {
        return;
      } else {
        return String(specialValue);
      }
    }

    if (convertFrom === 'GRADESCALE') {
      //Gradescale to gradescale is EASY!  Just return the item!!!
      return scoreValue;
    }

    if (gradeScale.gradeScaleType === 'NUMERIC') {
      switch (convertFrom) {
        case 'POINTS':
          if (pointsPossible == null) {
            return;
          }
          return convertPointsToNumericGradeScaleItem(Number(scoreValue), pointsPossible, gradeScale, calcRelationship, roundNumbers);
        case 'PERCENT':
          return convertPercentToNumericGradeScaleItem(Number(scoreValue), gradeScale, roundNumbers);
        case 'COLLECTED_ONLY':
          break;
        default:
          break;
      }
    } else {
      //gradeScale.gradeScaleType === 'ALPHA'
      switch (convertFrom) {
        case 'POINTS':
          if (pointsPossible == null) {
            return;
          }
          return convertPointsToGradeScaleItem(Number(scoreValue), pointsPossible, gradeScale);
        case 'PERCENT':
          return convertPercentToGradeScaleItem(Number(scoreValue), gradeScale);
        case 'COLLECTED_ONLY':
          break;
        default:
          break;
      }
    }
  }

  /**
   * Works for any grade scale, but if the exact item is not found then it returns undefined.
   */
  export function getGradeScaleItem(gradeScaleItemString: string | undefined, gradeScale: GradeScaleRI | undefined): GradeScaleItemRI | undefined {
    if (gradeScaleItemString == null || gradeScale == null) {
      return;
    }

    let gradeScaleItemUpperCaseString = gradeScaleItemString.toUpperCase();

    for (let gradeScaleItem of gradeScale.gradeScaleItems) {
      if (gradeScaleItem.name.toUpperCase() === gradeScaleItemUpperCaseString) {
        return gradeScaleItem;
      }
    }
  }

  export function convertNumericValueToNumericValue(numericValue: number | undefined, gradeScale: GradeScaleRI): number | undefined {
    if (numericValue == null) {
      return;
    }

    for (let gradeScaleItem of gradeScale.gradeScaleItems) {
      if (gradeScaleItem.numericCutOff <= numericValue) {
        return gradeScaleItem.numericValue;
      }
    }
  }

  export function convertNumericValueTo(convertTo: AssignmentScoreType,
    numericValue: number | undefined,
    numericGradeScale: GradeScaleRI,
    calcRelationship: CalcRelationship,
    roundNumbers: boolean,
    pointsPossible?: number): number | string | undefined {
    if (convertTo == null || numericValue == null || numericGradeScale == null) {
      return;
    }

    if (convertTo === 'GRADESCALE' && (hasNumericDecimals(numericGradeScale) || numericGradeScale.conversionToNumeric === 'PERCENT')) {
      return NumberUtil.round(numericValue, <number>(hasNumericDecimals(numericGradeScale) ? numericGradeScale.numericDecimals : 2));
    }

    if (numericGradeScale.gradeScaleType !== 'NUMERIC') {
      console.error('convertNumericValueTo expects a Numeric gradeScaleType');
      return;
    }

    switch (convertTo) {
      case 'POINTS':
      case 'PERCENT':
        return convertGradeScaleItemToScoreOfType(String(numericValue), numericGradeScale, convertTo, roundNumbers, calcRelationship, pointsPossible);
      case 'GRADESCALE':
        let gradeScaleItem = numericGradeScale.gradeScaleItems.find((gradeScaleItem: GradeScaleItemRI): boolean => {
          return gradeScaleItem.numericCutOff <= numericValue;
        });

        if (gradeScaleItem == null) {
          return;
        }

        if (!isNaN(<any>gradeScaleItem.name)) {
          return Number(gradeScaleItem.name);
        } else {
          return gradeScaleItem.name;
        }
      default: //shut up tslint
    }
  }

  export function convertNumericGradeScaleItemToNumericValue(gradeScaleItem: string | undefined, gradeScale: GradeScaleRI): number | undefined {
    if (gradeScaleItem == null) {
      return;
    }
    if (!isNaN(<any>gradeScaleItem)) {
      return Number(gradeScaleItem);
    } else {
      let item: GradeScaleItemRI | undefined = getGradeScaleItem(gradeScaleItem, gradeScale);
      if (item != null) {
        return item.numericValue;
      }
    }
  }

  /**
   * Gets the numeric grade scale item. If not found and the gradeScaleItemString is something that is numeric and larger
   * or smaller than the max and min. Then the numeric max or min will be returned.
   */
  export function getNumericGradeScaleItem(gradeScaleItemString: string | undefined, gradeScale: GradeScaleRI): GradeScaleItemRI | undefined {
    if (gradeScaleItemString == null) {
      return;
    }

    let gradeScaleItem = getGradeScaleItem(gradeScaleItemString, gradeScale);
    if (gradeScaleItem != null) {
      return gradeScaleItem;
    }

    if (gradeScaleItemString !== '' &&
      gradeScaleItemString !== String(gradeScale.numericMax) &&
      gradeScaleItemString !== String(gradeScale.numericMin)) {

      gradeScaleItemString = gradeScaleItemString.replace(PLUS_MINUS_REGEX, '');
      if (!isNaN(<any>gradeScaleItemString)) {
        let itemAsNumber: number = Number(gradeScaleItemString);

        if (itemAsNumber > gradeScale.numericMax) {
          return getGradeScaleItem(String(gradeScale.numericMax), gradeScale);
        }

        if (itemAsNumber < gradeScale.numericMin) {
          return getGradeScaleItem(String(gradeScale.numericMin), gradeScale);
        }
      }
    }
  }

  //This works for both ALPHA.  Call convertNumericGradeScalItemToPercent if you want that.
  //The percent returned is from 0 - 100
  export function convertGradeScaleItemToPercent(gradeScaleItemToConvert: string | undefined, gradeScale: GradeScaleRI, roundNumber: boolean): number | undefined {
    if (gradeScaleItemToConvert == null) {
      return;
    }
    let gradeScaleItem: GradeScaleItemRI | undefined = getGradeScaleItem(gradeScaleItemToConvert, gradeScale);
    if (gradeScaleItem != null) {
      return roundToPrecision(gradeScaleItem.percentValue, 2, PRECISION, roundNumber);
    }
  }

  export function convertNumericGradeScaleItemToPercent(gradeScaleItemToConvert: string | undefined, gradeScale: GradeScaleRI, roundNumber: boolean): number | undefined {
    if (gradeScaleItemToConvert == null) {
      return;
    }
    let gradeScaleItem: GradeScaleItemRI | undefined = getNumericGradeScaleItem(gradeScaleItemToConvert, gradeScale);
    if (gradeScaleItem != null) {
      return roundToPrecision(gradeScaleItem.percentValue, 2, PRECISION, roundNumber);
    }

    //if we can't find it, try to convert using ratios.
    return convertCalculatedNumericValueToPercentUsingRatios(gradeScaleItemToConvert, gradeScale, roundNumber);
  }

  export function convertGradeScaleItemToPoints(gradeScaleItemToConvert: string | undefined, gradeScale: GradeScaleRI, pointsPossible: number, roundNumber: boolean): number | undefined {
    if (gradeScaleItemToConvert == null) {
      return;
    }
    let percent = convertGradeScaleItemToPercent(gradeScaleItemToConvert, gradeScale, false);
    if (percent == null) {
      return;
    }
    return roundToPrecision(percent / 100 * pointsPossible, 2, PRECISION, roundNumber);
  }

  /**
   * @returns points
   */
  export function convertNumericGradeScaleItemToPoints(gradeScaleItemToConvert: string | undefined,
    gradeScale: GradeScaleRI,
    calcRelationship: CalcRelationship,
    pointsPossible: number,
    roundNumber: boolean): number | undefined {
    if (gradeScaleItemToConvert == null) {
      return;
    }

    if (calcRelationship === 'LEVELS') {
      let gradeScaleItemAsNumber: number | undefined;
      let gradeScaleItem: GradeScaleItemRI | undefined = getNumericGradeScaleItem(gradeScaleItemToConvert, gradeScale);

      if (gradeScaleItem != null) {
        gradeScaleItemAsNumber = gradeScaleItem.numericValue;
      } else if (!isNaN(<any>gradeScaleItemToConvert)) {
        gradeScaleItemAsNumber = Number(gradeScaleItemToConvert);
      }

      if (gradeScaleItemAsNumber != null) {
        if (gradeScaleItemAsNumber > gradeScale.numericMax) {
          gradeScaleItemAsNumber = <number>gradeScale.numericMax;
        }

        if (gradeScaleItemAsNumber < gradeScale.numericMin) {
          gradeScaleItemAsNumber = <number>gradeScale.numericMin;
        }

        return roundToPrecision(pointsPossible * gradeScaleItemAsNumber / gradeScale.numericMax, 2, PRECISION, roundNumber);
      }
      return; //If we didn't get it right using levels, we just return undefined.
    }

    //Don't round this, we want all the decimals for the points conversion
    let percent: number | undefined = convertNumericGradeScaleItemToPercent(gradeScaleItemToConvert, gradeScale, false);
    if (percent != null) {
      return roundToPrecision(percent / 100 * pointsPossible, 2, PRECISION, roundNumber);
    }
  }

  export function convertPointsToGradeScaleItem(points: number | undefined, pointsPossible: number, gradeScale: GradeScaleRI): string | undefined {
    if (points == null) {
      return;
    }

    //between 0 and 100;
    let percent = NumberUtil.round(points / pointsPossible * 100, PRECISION);
    return convertPercentToGradeScaleItem(percent, gradeScale);
  }

  //percent between 0 and 100;
  export function convertPercentToGradeScaleItem(percent: number | undefined, gradeScale: GradeScaleRI): string | undefined {
    if (percent == null) {
      return;
    }

    for (let gradeScaleItem of gradeScale.gradeScaleItems) {
      if (gradeScaleItem.cutOffPercentage <= percent) {
        return gradeScaleItem.name;
      }
    }
  }

  //percent between 0 and 100
  export function convertPercentToNumericGradeScaleItem(percent: number | undefined, gradeScale: GradeScaleRI, roundNumber: boolean): string | undefined {
    if (percent == null) {
      return;
    }

    if (gradeScale.conversionToNumeric === 'PERCENT' || hasNumericDecimals(gradeScale)) {
      return convertPercentToNumericGradeScaleItemUsingRatios(percent, gradeScale, roundNumber);
    } else {
      return convertPercentToGradeScaleItem(percent, gradeScale);
    }
  }

  export function convertPointsToNumericGradeScaleItem(points: number | undefined,
    pointsPossible: number,
    gradeScale: GradeScaleRI,
    calcRelationship: CalcRelationship,
    roundNumber: boolean): string | undefined {
    if (points == null) {
      return;
    }

    let pointsAsPercentOfTotalPercent = NumberUtil.round(points / pointsPossible, PRECISION);

    if (calcRelationship === 'LEVELS') {
      if (gradeScale.conversionToNumeric === 'PERCENT' || hasNumericDecimals(gradeScale)) {
        //This calc will return a decimal answer
        let decimals = 2;
        if (gradeScale.numericDecimals != null && hasNumericDecimals(gradeScale)) {
          decimals = gradeScale.numericDecimals;
        }
        //Ratios calc or numeric with decimals
        let numericValue: number = NumberUtil.roundToPrecision(pointsAsPercentOfTotalPercent * gradeScale.numericMax, decimals, PRECISION, roundNumber);

        if (gradeScale.numericMax != null && numericValue > gradeScale.numericMax) {
          return String(gradeScale.numericMax);
        }

        if (gradeScale.numericMin != null && numericValue < gradeScale.numericMin) {
          return String(gradeScale.numericMin);
        }

        return getMatchedNumericGradeScaleItem(numericValue, gradeScale);
      } else {
        //This calc will return something that matches a grade scale item.
        for (let gradeScaleItem of gradeScale.gradeScaleItems) {
          //percent between 0 and 1
          let percent;
          if (isNaN(<any>gradeScaleItem.name)) {
            percent = NumberUtil.round(gradeScaleItem.numericValue / gradeScale.numericMax, PRECISION);
          } else {
            percent = NumberUtil.round(Number(gradeScaleItem.name) / gradeScale.numericMax, PRECISION);
          }

          if (percent <= pointsAsPercentOfTotalPercent) {
            return gradeScaleItem.name;
          }
        }
        //If we didn't get anything, we just use the last item in the scale.
        return gradeScale.gradeScaleItems.slice(-1)[0].name;
      }
    } else { //calcRelationship === 'GRADESCALE'

      if (gradeScale.conversionToNumeric === 'PERCENT' || hasNumericDecimals(gradeScale)) {
        //Ratios calc or numeric with decimals
        //This means we return decimal answers.
        return convertPercentToNumericGradeScaleItemUsingRatios(pointsAsPercentOfTotalPercent * 100, gradeScale, roundNumber);
      } else {

        return convertPointsToGradeScaleItem(points, pointsPossible, gradeScale);
      }
    }
  }

  export function convertPercentToNumericGradeScaleItemUsingRatios(percent: number | undefined, gradeScale: GradeScaleRI, roundNumber: boolean): string | undefined {
    if (percent == null) { return; }

    let calculatedGsi: number | undefined = convertPercentToCalculatedNumericGradeScaleItemUsingRatios(percent, gradeScale, roundNumber);

    return getMatchedNumericGradeScaleItem(calculatedGsi, gradeScale);
  }

  function getMatchedNumericGradeScaleItem(numericValue: number | undefined, gradeScale: GradeScaleRI): string | undefined {
    if (numericValue == null) {
      return;
    }
    //If numeric decimals isn't set, or is 0.
    if (!gradeScale.numericDecimals) {
      //See if I can can find an actual gradeScaleItem.
      for (let gradeScaleItem of gradeScale.gradeScaleItems) {
        if ((gradeScaleItem.numericValue + .05) >= numericValue &&
         (gradeScaleItem.numericValue - .05) <= numericValue) {
          return gradeScaleItem.name;
        }
      }
    }
    return String(numericValue);
  }

  //percent between 0 and 100
  export function convertPercentToCalculatedNumericGradeScaleItemUsingRatios(percent: number | undefined, gradeScale: GradeScaleRI, roundNumber: boolean): number | undefined {
    if (percent == null) { return; }
    let numberOfDecimals: number = gradeScale.numericDecimals ? gradeScale.numericDecimals : 2;
    let highGrade: GradeScaleItemRI | undefined = undefined;
    let lowGrade: GradeScaleItemRI | undefined = undefined;

    let grades: Array<GradeScaleItemRI> = gradeScale.gradeScaleItems;
    for (let grade of grades) {
      if (percent === grade.cutOffPercentage) {
        return grade.numericValue;
      }

      if (percent > grade.cutOffPercentage) {
        lowGrade = grade;
        break;
      } else {
        highGrade = grade;
      }
    }

    if (lowGrade == null && highGrade == null) {
      return roundToPrecision(getDefaultNumericGrade(percent, gradeScale), numberOfDecimals, PRECISION, roundNumber);
    }

    if (lowGrade != null && highGrade == null) {
      return roundToPrecision(lowGrade.numericValue, numberOfDecimals, PRECISION, roundNumber);
    }

    if (lowGrade == null && highGrade != null) {
      return roundToPrecision(highGrade.numericValue, numberOfDecimals, PRECISION, roundNumber);
    }

    if (lowGrade != null && highGrade != null) {
      let high: number = highGrade.numericValue;
      let low: number = lowGrade.numericValue;

      //both aren't null, so now its time to interpolate the new score based on low / high
      let highPercent: number = highGrade.cutOffPercentage;
      let lowPercent: number = lowGrade.cutOffPercentage;

      // given a percent score, calculate a score between low and high (numerics)
      return roundToPrecision(interpolateForYGivenX(percent, lowPercent, low, highPercent, high), numberOfDecimals, PRECISION, roundNumber);
    }
  }

  //returns 0-100
  export function convertCalculatedNumericValueToPercentUsingRatios(numericGradeScaleItem: string | undefined, gradeScale: GradeScaleRI, roundNumber: boolean): number | undefined {
    if (isNaN(<any>numericGradeScaleItem) || numericGradeScaleItem == null) {
      return;
    }

    let highGrade: GradeScaleItemRI | undefined = undefined;
    let lowGrade: GradeScaleItemRI | undefined = undefined;
    let gradeScaleItemAsNumber = Number(numericGradeScaleItem);

    for (let grade of gradeScale.gradeScaleItems) {
      if (numericGradeScaleItem === grade.name) {
        return grade.percentValue;
      }

      if (gradeScaleItemAsNumber > grade.numericValue) {
        lowGrade = grade;
        break;
      } else {
        highGrade = grade;
      }
    }

    if (lowGrade == null && highGrade == null) {
      return roundToPrecision(getDefaultNumericGrade(0, gradeScale), 2, PRECISION, roundNumber);
    }

    if (lowGrade != null && highGrade == null) {
      return roundToPrecision(lowGrade.percentValue, 2, PRECISION, roundNumber);
    }

    if (lowGrade == null && highGrade != null) {
      return roundToPrecision(highGrade.percentValue, 2, PRECISION, roundNumber);
    }

    if (lowGrade != null && highGrade != null) {
      let high: number = highGrade.numericValue;
      let low: number = lowGrade.numericValue;

      //both aren't null, so now its time to interpolate the new score based on low / high
      let highPercent: number = highGrade.percentValue;
      let lowPercent: number = lowGrade.percentValue;

      // given a percent score, calculate a score between low and high (numerics)
      return roundToPrecision(interpolateForYGivenX(gradeScaleItemAsNumber, low, lowPercent, high, highPercent), 2, PRECISION, roundNumber);
    }
  }

  export function getDefaultNumericGrade(percent: number, scale: GradeScaleRI): number {
    //TODO: Brent will add some special logic in here.
    return <number>scale.numericMin;
  }

  /**
   * this is a classic linear interpolation. Given a pair of (x,y) points on the gradescale
   * and an x value, calculate the new y value.
   * See http://en.wikipedia.org/wiki/Linear_interpolation.
   * @param x  - The x value (score you know)
   * @param x0 - low value on x axis
   * @param y0 - low value on y axis
   * @param x1 - high value on x axis
   * @param y1 - high value on the y axis
   * @return y
   */
  export function interpolateForYGivenX(x: number, x0: number, y0: number, x1: number, y1: number): number {
    let y: number;
    try {
      y = NumberUtil.round(y0 + (y1 - y0) * (x - x0) / (x1 - x0), PRECISION);
    } catch (e) {
      throw 'ArithmeticException Error Interpolating for Y given x, x0, y0, x1, y1: ' + x + ',' + x0 + ',' + y0 + ',' + x1 + ',' + y1;
    }

    if (isNaN(y) || !isFinite(y)) {
      throw 'Not a Number or Infinite result:  Interpolating for Y given x, x0, y0, x1, y1: ' + x + ',' + x0 + ',' + y0 + ',' + x1 + ',' + y1;
    }

    return y;
  }

  /**
   * @param validationEngine - When calling this on the client side, pass in the validation object so it has the correct
   * locale.  No need to pass in on the server side unless there is a specific reason to.
   * @param scoreType
   * @param gradeScale
   * @returns {any}
   */
  export function generateValidators(scoreType: AssignmentScoreType, gradeScale: GradeScaleRI, maxPoints?: number, validationEngine?: ValidationRulesEngine): Array<Validator> {
    if (validationEngine == null) {
      validationEngine = new ValidationRulesEngine();
    }
    let validators: Array<Validator>;

    let specialGradeScaleValidator: Validator | undefined;
    if (gradeScale.specialGradeScale != null) {
      let specialScale = gradeScale.specialGradeScale;
      specialGradeScaleValidator = {
        execute: function validateSpecialGradeScale(value: string, condition: any, context: { [key: string]: any }): ValidationResult {
          let ignoreSubsequentValidators = false;
          if (value != null) {
            let valueUpperCase = value.toUpperCase();
            ignoreSubsequentValidators = specialScale.gradeScaleItems.find((item: GradeScaleItemRI): boolean => {
              return item.name.toUpperCase() === valueUpperCase;
            }) != null;
          }
          return {
            isValid: true,
            ignoreSubsequentValidators: ignoreSubsequentValidators
          };
        }
      };
    }

    switch (scoreType) {
      case 'POINTS':
        validators = validationEngine.getValidatorsForRules({
          isNumber: true,
          minValue: 0,
          maxValue: maxPoints,
          maxDecimals: 2
        });

        if (specialGradeScaleValidator != null) {
          validators.unshift(specialGradeScaleValidator);
        }

        // When max points is set to zero, treat any entered value as invalid.
        if (maxPoints === 0) {
          validators.unshift({
            execute: function validateMaxPoints(value: string, condition: any, context: { [key: string]: any }): ValidationResult {
              return {
                isValid: false,
                ignoreSubsequentValidators: true,
                errorMessage: this.i18n.getText('cu|grade scale util|Score entered is greater than maximum points.')
              };
            }
          });
        }
        return validators;

      case 'PERCENT':
        validators = validationEngine.getValidatorsForRules({
          isNumber: true,
          minValue: 0,
          maxValue: 200,
          maxDecimals: 2
        });

        if (specialGradeScaleValidator != null) {
          validators.unshift(specialGradeScaleValidator);
        }
        return validators;

      case 'GRADESCALE':
        let validationRules: ValidationRules = {};
        let preValidator: Validator | undefined;
        let postValidator: Validator | undefined;
        if (gradeScale.gradeScaleType === 'NUMERIC') {
          validationRules = {
            isNumber: true,
            maxValue: gradeScale.numericMax,
            minValue: gradeScale.numericMin
          };

          if (gradeScale.numericDecimals != null && hasNumericDecimals(gradeScale)) {
            validationRules.maxDecimals = gradeScale.numericDecimals;
          } else if (gradeScale.conversionToNumeric === 'PERCENT') {
            validationRules.maxDecimals = 2;
          }

          let localizedGradeScaleItems: Array<string> = gradeScale.gradeScaleItems.map((gradeScaleItem: GradeScaleItemRI) => {
            if (!isNaN(<any>gradeScaleItem.name)) {
              //validationEngine IS defined because if it starts as undefined, one is created right at the beginning of the function.
              let localizedNum:
                string
                | undefined = validationEngine!.i18n.localizeNumber(Number(<string>gradeScaleItem.name));
              if (localizedNum != null) {
                return localizedNum;
              }
            }
            return gradeScaleItem.name;
          });

          preValidator = {
            execute: function numericScalePreValidator(value: string, condition: any, context: { [key: string]: any }): ValidationResult {
              let validationResult: ValidationResult = {
                isValid: true
              };

              if (localizedGradeScaleItems.find((gradeScaleItemName: string) => {
                return gradeScaleItemName === value;
              }) != null) {
                //If we find the item, no need to test any of the other rules.
                validationResult.ignoreSubsequentValidators = true;
              }

              return validationResult;
            }
          };

          if (!gradeScale.numericDecimals && gradeScale.conversionToNumeric !== 'PERCENT') {
            postValidator = {
              execute: function numericScalePostValidator(value: string, condition: any, context: { [key: string]: any }): ValidationResult {
                //Really, if it's not a numeric scale, and not using the Ratios calc, then this should match a grade scale item.
                //It didn't match and exit in the preValidator, so we return false here.
                //So why didn't we just return invalid in the preValidator?  It's because the other numeric rules may still fail
                //before we get to this validator which will give the user a better error message.
                let validationResult: ValidationResult = {
                  isValid: false,
                  errorMessage: validationEngine!.i18n.getText('cu|grade scale util|Invalid')
                };

                return validationResult;
              }
            };
          }
        } else {
          //* * * * * * ALPHA GRADE SCALES * * * * * *
          validationRules.isText = true;
          validationRules.maxLength = 7;
          preValidator = {
            execute: function alphaScalePreValidator(value: string, condition: any, context: { [key: string]: any }): ValidationResult {
              let valueUpperCase: string | undefined;
              let isValid = false;
              if (value != null) {
                valueUpperCase = value.toUpperCase();
                isValid = gradeScale.gradeScaleItems.find((gradeScaleItem: GradeScaleItemRI) => {
                  return gradeScaleItem.name.toUpperCase() === valueUpperCase;
                }) != null;
              }

              let validationResult: ValidationResult = {
                isValid: isValid
              };

              if (!validationResult.isValid) {
                validationResult.errorMessage = validationEngine!.i18n.getText('cu|grade scale util|Invalid');
              }

              return validationResult;
            }
          };
        }

        //Generate the validators
        validators = validationEngine.getValidatorsForRules(validationRules);

        //Add in the custom validators if applicable.
        if (preValidator != null) {
          validators.unshift(preValidator);
        }

        //Special Scales validate before the preValidator in this case as they do not ever say something is invalid.
        if (specialGradeScaleValidator != null) {
          validators.unshift(specialGradeScaleValidator);
        }

        if (postValidator != null) {
          validators.push(postValidator);
        }
        return validators;

      case 'COLLECTED_ONLY':
        validators = [{
          execute: function isEmptyOrUndefined(value: string, condition: any, context: { [key: string]: any }): ValidationResult {
            let validationResult: ValidationResult = {
              isValid: value.length <= 0
            };

            if (!validationResult.isValid) {
              validationResult.errorMessage = validationEngine!.i18n.getText('cu|grade scale util|Collected Only assignments cannot accept scores.');
            }

            return validationResult;
          }
        }];
        return validators;

      default:
        return [];
    }
  }

  export function getColorLevel(gradeScaleScore: string, gradeScale: GradeScaleRI): string | undefined {
    if (gradeScaleScore == null || gradeScaleScore.trim().length === 0 || gradeScale == null) {
      return;
    }

    let gradeScaleItem: GradeScaleItemRI | undefined;

    if (gradeScale.gradeScaleType === 'ALPHA') {
      gradeScaleItem = getGradeScaleItem(gradeScaleScore, gradeScale);
    } else {
      gradeScaleItem = getNumericGradeScaleItem(gradeScaleScore, gradeScale);

      if (gradeScaleItem == null && !isNaN(<any>gradeScaleScore)) {
        let itemAsNumber: number = Number(gradeScaleScore);
        for (let gsi of gradeScale.gradeScaleItems) {
          if (itemAsNumber >= gsi.numericCutOff) {
            gradeScaleItem = gsi;
            break;
          }
        }
      }
    }

    if (gradeScaleItem != null) {
      return gradeScaleItem.color;
    }
  }

  /**
   * Gets the special grade scale item if its applicable for this score.
   * Such as, if 'inc' is an item in both the grade scale and the special scale, this method would return undefined.
   */
  export function getSpecialGradeScaleItem(scoreValue: string | undefined,
    gradeScale: GradeScaleRI,
    scoreType: AssignmentScoreType): SpecialGradeScaleItemRI | undefined {
    let specialCode: SpecialGradeScaleItemRI | undefined = <SpecialGradeScaleItemRI | undefined>getGradeScaleItem(scoreValue, gradeScale.specialGradeScale);
    if (specialCode == null) {
      return;
    }

    //So we have the item. The question is, is the item part of the normal grade scale.
    switch (scoreType) {
      case 'POINTS':
      case 'PERCENT':
        //If the score value is a number so we will assume it is not a special scale item.
        if (!isNaN(<any>scoreValue)) {
          return;
        }
        break;
      case 'GRADESCALE':
        if (getGradeScaleItem(scoreValue, gradeScale) != null) {
          return;
        }

        if (gradeScale.gradeScaleType === 'NUMERIC' && !isNaN(<any>scoreValue) && (gradeScale.conversionToNumeric === 'PERCENT' || hasNumericDecimals(gradeScale))) {
          let numericScore: number = Number(scoreValue);
          if (numericScore >= gradeScale.numericMin &&
            numericScore <= gradeScale.numericMax) {
            //At this point we know the score is a valid numeric value even though it is not one of the grade scale items.
            return;
          }
        }
        break;
      default: //shut up tslint
    }

    return specialCode;
  }

  //GradeScaleItem -> points  (needed for inspectorScore.getGrade)
  //GradeScaleItem -> percent (needed for inspectorScore.getGrade)
  //GradeScaleItem -> NumericGradeScaleItem (as string) (needed for convertToGradeScale)
  //GradeScaleItem -> AlphaGradeScaleItem (as string) (needed for convertToGradeScale)
  export function convertSpecialGradeScaleItemTo(specialGradeScaleItem: SpecialGradeScaleItemRI,
    gradeScale: GradeScaleRI,
    convertTo: AssignmentScoreType,
    roundNumbers: boolean,
    calcRelationship: CalcRelationship,
    pointsPossible?: number): string | number | undefined {
    if (specialGradeScaleItem.isExempt) {
      //Exempt stuff doesn't convert into anything.
      return;
    }

    switch (convertTo) {
      case 'POINTS':
        return NumberUtil.roundToPrecision(specialGradeScaleItem.percentValue / 100 * pointsPossible, 2, PRECISION, roundNumbers);
      case 'PERCENT':
        return NumberUtil.roundToPrecision(specialGradeScaleItem.percentValue, 2, PRECISION, roundNumbers);
      case 'GRADESCALE':
        if (gradeScale.gradeScaleType === 'ALPHA') {
          return convertPercentToGradeScaleItem(specialGradeScaleItem.percentValue, gradeScale);
        } else {
          if (specialGradeScaleItem.numericType === 'MAX') {
            return gradeScale.numericMax;
          } else if (specialGradeScaleItem.numericType === 'MIN') {
            return gradeScale.numericMin;
          } else {
            return convertNumericValueTo('GRADESCALE', specialGradeScaleItem.numericValue, gradeScale, calcRelationship, true);
          }
        }
      default: //shut up tslint
    }
  }

  export function hasNumericDecimals(gradeScale: GradeScaleRI): boolean {
    if (gradeScale != null && gradeScale.numericDecimals == null) {
      return false;
    }
    return gradeScale.numericDecimals > 0;
  }

  export function gradeScaleValueExpression(gradeScale: GradeScaleRI, i18nUtil?: I18nUtil): string {
    if (i18nUtil == null) {
      i18nUtil = new I18nUtil();
    }
    if (gradeScale.gradeScaleType === 'NUMERIC') {
      let extras = '';
      switch (gradeScale.numericExtras) {
        case 'QUARTER_POINT_25':
          extras = ' (' + i18nUtil.localizeNumber(.25) + ')';
          break;
        case 'THIRD_POINT_33':
          extras = ' (' + i18nUtil.localizeNumber(.33) + ')';
          break;
        case 'HALF_POINT_5':
          extras = ' (' + i18nUtil.localizeNumber(.5) + ')';
          break;
        case 'HALF_PLUS':
          extras = ' (+)';
          break;
        case 'THIRD_PLUS_MINUS':
          extras = ' (+/-)';
          break;
        default:
      }
      let decimals = gradeScale.numericDecimals ? gradeScale.numericDecimals : 0;
      let min = gradeScale.numericMin ? gradeScale.numericMin : 0;
      let max = gradeScale.numericMax ? gradeScale.numericMax : 0;
      return (i18nUtil.localizeNumber(min, decimals, decimals > 0) + '-' + i18nUtil.localizeNumber(max, decimals, decimals > 0) + extras);
    } else {
      return (gradeScale.gradeScaleItems[0].name + '-' + gradeScale.gradeScaleItems[gradeScale.gradeScaleItems.length - 1].name);
    }
  }
}
